import type { AccessConditionGroup } from '../co-rules/models.ts';

export interface BookingResourceLocationOption {
    ID: string | number;
    Name: string;
}

export interface BookingResourceTaxRate {
    ID: string;
    DisplayName: string;
    Inclusive: boolean;
    Percentage: number;
}

export interface BookingResourceBookingTime {
    From: string;
    To: string;
    Weekdays: number[];
}

export const BookingGranularityType = {
    BookingGranularityUndefined: 0,
    BookingGranularity15Minutes: 1,
    BookingGranularity30Minutes: 2,
    BookingGranularityHour: 3,
    BookingGranularityDay: 4,
} as const;

export type BookingGranularityType = typeof BookingGranularityType[keyof typeof BookingGranularityType];

export const CheckOutDayType = {
    CheckOutDayUndefined: 0,
    CheckOutDaySameDay: 1,
    CheckOutDayFollowingDay: 2,
} as const;

export type CheckOutDayType = typeof CheckOutDayType[keyof typeof CheckOutDayType];

export interface BookingResourceTimeOfDay {
    Hour: number;
    Minute: number;
}

export interface BookingResourceDayBookingConfiguration {
    CheckInTime: BookingResourceTimeOfDay;
    CheckOutTime: BookingResourceTimeOfDay;
    CheckOutDay: CheckOutDayType;
}

export interface BookingResourceBookingTimeOverrideEntry {
    From: string;
    To: string;
    Block: boolean;
    IsAllDay: boolean;
}

export interface BookingResourceDateOverrideDraft {
    date: string | null;
    from: string;
    to: string;
    Block: boolean;
    IsAllDay: boolean;
}

export interface BookingResourceAfterBooking {
    Title: string;
    Text: string;
    CallToActionURL: string;
    CallToActionTitle: string;
}

export interface BookingResourceBlockingRule {
    BlockResourceID: string;
    NumberOfSlots: number;
}

export interface BookingResourceCalendarToken {
    ID: string;
    CreatedAt?: number;
    Name?: string;
}

export interface BookingResourceExternalCalendar {
    ID: string;
    Name: string;
    TokenID?: string;
    tokenID?: string;
    SyncToken?: string;
    Subscribed?: boolean;
    ChannelExpiration?: number;
    LastSync?: number;
}

export interface BookingResourceLockOption {
    ID: string;
    Name: string;
    Provider?: string;
}

export interface BookingResourceLockAssignment {
    ID: string;
    Name: string;
    Action: number;
    BeforeUnitXTimesBeforBookingStart: number;
    BeforeUnit: number | string | null;
    AfterUnitXTimesAfterBookingEnd: number;
    AfterUnit: number | string | null;
}

export interface BookingResourceUpgrade {
    ID?: string;
    OriginalID?: string;
    Name: string;
    Description?: string;
    MinAmount?: number;
    MaxAmount?: number;
}

export interface BookingResource {
    ID?: string;
    Name: string;
    Description: string;
    ImageURL: string | null;
    LocationID: string | number | null;
    Hidden: boolean;
    PriceInCentsPerHour: number;
    Currency: string;
    TaxRateID: string | null;
    Capacity: number;
    CancellationPeriod: number;
    AllowStartedTimeSlotBooking: boolean;
    MinBookingDuration: number;
    MaxBookingDuration: number;
    BookingGranularity: BookingGranularityType;
    DayBooking: BookingResourceDayBookingConfiguration | null;
    BookingTimes: BookingResourceBookingTime[];
    BookingTimeOverride: Record<string, BookingResourceBookingTimeOverrideEntry>;
    BookWholeSlot: boolean;
    PricePerSlot: boolean;
    SendEmailAfterBooking: boolean;
    NotifyEmails: string[];
    ChargeForLikedResources: boolean;
    AfterBookingEmailContent: string;
    AfterBooking: BookingResourceAfterBooking;
    Rules: BookingResourceBlockingRule[];
    ExternalCalendars: BookingResourceExternalCalendar[];
    TermsAndConditionsLink: string;
    Locks: BookingResourceLockAssignment[];
    Upgrades: BookingResourceUpgrade[];
    WhoCanSee: AccessConditionGroup[];
    WhoCanSeeCombineWithOR: boolean;
    WhoCanBook: AccessConditionGroup[];
    WhoCanBookCombineWithOR: boolean;
}

export interface BookingResourceWeekdayOption {
    label: string;
    value: number;
}

export const DEFAULT_AFTER_BOOKING_EMAIL_CONTENT =
    'Thanks for your booking!\n\nWhat: {{resource_name}}\nUpgrades: {{upgrades}}\nFrom: {{from}}\nTo: {{to}}\nComment: {{comment}}';

export const BOOKING_RESOURCE_WEEKDAYS: BookingResourceWeekdayOption[] = [
    { label: 'Mo', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Th', value: 4 },
    { label: 'Fr', value: 5 },
    { label: 'Sa', value: 6 },
    { label: 'Su', value: 7 },
];

export function createBookingResourceBookingTime(
    overrides: Partial<BookingResourceBookingTime> = {}
): BookingResourceBookingTime {
    const normalizedOverrides = {
        ...overrides,
        // Preserve an explicit empty weekday list so fully deactivated availability stays empty.
        Weekdays: Array.isArray(overrides.Weekdays) ? [...overrides.Weekdays] : [1, 2, 3, 4, 5],
    };

    return {
        From: '00:00',
        To: '00:00',
        Weekdays: [1, 2, 3, 4, 5],
        ...normalizedOverrides,
    };
}

export function cloneBookingResourceBookingTime(
    bookingTime: Partial<BookingResourceBookingTime> = {}
): BookingResourceBookingTime {
    return createBookingResourceBookingTime(bookingTime);
}

export function createBookingResourceTimeOfDay(
    overrides: Partial<BookingResourceTimeOfDay> = {}
): BookingResourceTimeOfDay {
    return {
        Hour: 9,
        Minute: 0,
        ...overrides,
    };
}

export function cloneBookingResourceTimeOfDay(
    timeOfDay: Partial<BookingResourceTimeOfDay> = {}
): BookingResourceTimeOfDay {
    return createBookingResourceTimeOfDay(timeOfDay);
}

export function createBookingResourceDayBookingConfiguration(
    overrides: Partial<BookingResourceDayBookingConfiguration> = {}
): BookingResourceDayBookingConfiguration {
    const normalizedOverrides = {
        ...overrides,
        CheckInTime: cloneBookingResourceTimeOfDay(overrides.CheckInTime),
        CheckOutTime: cloneBookingResourceTimeOfDay(overrides.CheckOutTime || { Hour: 17, Minute: 0 }),
    };

    return {
        // Provide valid defaults when the backend marks a resource as day-based
        // but does not yet send a dedicated day-booking configuration.
        CheckInTime: createBookingResourceTimeOfDay(),
        CheckOutTime: createBookingResourceTimeOfDay({ Hour: 17, Minute: 0 }),
        CheckOutDay: CheckOutDayType.CheckOutDaySameDay,
        ...normalizedOverrides,
    };
}

export function cloneBookingResourceDayBookingConfiguration(
    dayBooking: Partial<BookingResourceDayBookingConfiguration> = {}
): BookingResourceDayBookingConfiguration {
    return createBookingResourceDayBookingConfiguration(dayBooking);
}

export function createBookingResourceDateOverrideDraft(
    overrides: Partial<BookingResourceDateOverrideDraft> = {}
): BookingResourceDateOverrideDraft {
    return {
        date: null,
        from: '09:00',
        to: '17:00',
        Block: false,
        IsAllDay: false,
        ...overrides,
    };
}

export function cloneBookingResourceDateOverrideDraft(
    override: Partial<BookingResourceDateOverrideDraft> = {}
): BookingResourceDateOverrideDraft {
    return createBookingResourceDateOverrideDraft(override);
}

export function createBookingResourceAfterBooking(
    overrides: Partial<BookingResourceAfterBooking> = {}
): BookingResourceAfterBooking {
    return {
        Title: '',
        Text: '',
        CallToActionURL: '',
        CallToActionTitle: '',
        ...overrides,
    };
}

export function cloneBookingResourceAfterBooking(
    afterBooking: Partial<BookingResourceAfterBooking> = {}
): BookingResourceAfterBooking {
    return createBookingResourceAfterBooking(afterBooking);
}

export function cloneBookingResourceBookingTimeOverride(
    bookingTimeOverride: Record<string, BookingResourceBookingTimeOverrideEntry> = {}
): Record<string, BookingResourceBookingTimeOverrideEntry> {
    return Object.keys(bookingTimeOverride).reduce((result, key) => {
        const currentValue = bookingTimeOverride[key];
        return {
            ...result,
            [key]: {
                From: currentValue?.From || '09:00',
                To: currentValue?.To || '17:00',
                Block: !!currentValue?.Block,
                IsAllDay: !!currentValue?.IsAllDay,
            },
        };
    }, {});
}

export function cloneBookingResourceBlockingRule(
    rule: Partial<BookingResourceBlockingRule> = {}
): BookingResourceBlockingRule {
    return {
        BlockResourceID: rule.BlockResourceID || '',
        NumberOfSlots: rule.NumberOfSlots || 1,
    };
}

export function cloneBookingResourceExternalCalendar(
    calendar: Partial<BookingResourceExternalCalendar> = {}
): BookingResourceExternalCalendar {
    return {
        ID: calendar.ID || '',
        Name: calendar.Name || '',
        TokenID: calendar.TokenID || calendar.tokenID || '',
        tokenID: calendar.tokenID || calendar.TokenID || '',
        SyncToken: calendar.SyncToken || '',
        Subscribed: !!calendar.Subscribed,
        ChannelExpiration: calendar.ChannelExpiration || 0,
        LastSync: calendar.LastSync || 0,
    };
}

export function cloneBookingResourceLockAssignment(
    lock: Partial<BookingResourceLockAssignment> = {}
): BookingResourceLockAssignment {
    return {
        ID: lock.ID || '',
        Name: lock.Name || '',
        Action: typeof lock.Action === 'number' ? lock.Action : 0,
        BeforeUnitXTimesBeforBookingStart: lock.BeforeUnitXTimesBeforBookingStart || 0,
        BeforeUnit: lock.BeforeUnit ?? 0,
        AfterUnitXTimesAfterBookingEnd: lock.AfterUnitXTimesAfterBookingEnd || 0,
        AfterUnit: lock.AfterUnit ?? 0,
    };
}

export function cloneBookingResourceUpgrade(upgrade: Partial<BookingResourceUpgrade> = {}): BookingResourceUpgrade {
    return {
        ID: upgrade.ID || '',
        OriginalID: upgrade.OriginalID || upgrade.ID || '',
        Name: upgrade.Name || '',
        Description: upgrade.Description || '',
        MinAmount: upgrade.MinAmount || 0,
        MaxAmount: upgrade.MaxAmount || 0,
    };
}

export function createBookingResource(overrides: Partial<BookingResource> = {}): BookingResource {
    const bookingGranularity =
        typeof overrides.BookingGranularity === 'number'
            ? overrides.BookingGranularity
            : BookingGranularityType.BookingGranularityUndefined;
    const shouldCreateDayBooking = bookingGranularity === BookingGranularityType.BookingGranularityDay;
    const normalizedOverrides = {
        ...overrides,
        BookingGranularity: bookingGranularity,
        DayBooking:
            overrides.DayBooking || shouldCreateDayBooking
                ? cloneBookingResourceDayBookingConfiguration(overrides.DayBooking || {})
                : null,
        // Only default missing booking times; an explicit empty array means the resource is unavailable.
        BookingTimes: Array.isArray(overrides.BookingTimes)
            ? overrides.BookingTimes.map(cloneBookingResourceBookingTime)
            : [createBookingResourceBookingTime()],
        BookingTimeOverride: cloneBookingResourceBookingTimeOverride(overrides.BookingTimeOverride),
        NotifyEmails: overrides.NotifyEmails && overrides.NotifyEmails.length > 0 ? [...overrides.NotifyEmails] : [],
        AfterBooking: cloneBookingResourceAfterBooking(overrides.AfterBooking),
        Rules: overrides.Rules ? overrides.Rules.map(cloneBookingResourceBlockingRule) : [],
        ExternalCalendars: overrides.ExternalCalendars
            ? overrides.ExternalCalendars.map(cloneBookingResourceExternalCalendar)
            : [],
        Locks: overrides.Locks ? overrides.Locks.map(cloneBookingResourceLockAssignment) : [],
        Upgrades: overrides.Upgrades ? overrides.Upgrades.map(cloneBookingResourceUpgrade) : [],
        WhoCanSee: overrides.WhoCanSee ? JSON.parse(JSON.stringify(overrides.WhoCanSee)) : [],
        WhoCanBook: overrides.WhoCanBook ? JSON.parse(JSON.stringify(overrides.WhoCanBook)) : [],
    };

    return {
        Name: '',
        Description: '',
        ImageURL: null,
        LocationID: null,
        Hidden: false,
        PriceInCentsPerHour: 0,
        Currency: 'EUR',
        TaxRateID: null,
        Capacity: 1,
        CancellationPeriod: 0,
        AllowStartedTimeSlotBooking: false,
        MinBookingDuration: 0,
        MaxBookingDuration: 0,
        BookingGranularity: BookingGranularityType.BookingGranularityUndefined,
        DayBooking: null,
        BookingTimes: [createBookingResourceBookingTime()],
        BookingTimeOverride: {},
        BookWholeSlot: false,
        PricePerSlot: false,
        SendEmailAfterBooking: true,
        NotifyEmails: [],
        ChargeForLikedResources: false,
        AfterBookingEmailContent: DEFAULT_AFTER_BOOKING_EMAIL_CONTENT,
        AfterBooking: createBookingResourceAfterBooking(),
        Rules: [],
        ExternalCalendars: [],
        TermsAndConditionsLink: '',
        Locks: [],
        Upgrades: [],
        WhoCanSee: [],
        WhoCanSeeCombineWithOR: false,
        WhoCanBook: [],
        WhoCanBookCombineWithOR: false,
        ...normalizedOverrides,
    };
}

export function cloneBookingResource(resource: Partial<BookingResource> = {}): BookingResource {
    return createBookingResource(resource);
}
